<template>
    <div class="container-fluid pb-5">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 px-3 px-md-0">
            <div>
                <h3 class="fw-bold mb-1">Umowy kupna sprzedaży</h3>
                <p class="text-muted small mb-0">Lista umów kupna sprzedaży wystawionych na koncie</p>
            </div>
            <button
                class="btn btn-primary-dark d-flex align-items-center justify-content-center gap-2 w-100 btn-responsive-auto"
                @click="openModalForCreate()">
                <i class="bi bi-plus-lg"></i>
                <span>Nowa umowa sprzedaży</span>
            </button>
        </div>

        <!-- LISTA UMÓW SPRZEDAŻY -->
        <div class="card border shadow-sm">
            <div class="card-body">
                <div class="table-responsive">
                    <Vue3EasyDataTable :headers="headers" :items="contracts" :loading="loading" buttons-pagination show-index>
                        <template #item-companyName="item">
        <span>
            {{ item.companyName ? item.companyName : `${item.firstName || ''} ${item.lastName || ''}`.trim() || '—' }}
        </span>
    </template>
                        <template #item-actions="item">
                            <div class="d-flex justify-content-end gap-1 py-1">
                                <button class="btn btn-sm btn-outline-primary" title="Edytuj umowę" @click="editContract(item)">
                                    <i class="bi bi-pencil"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-secondary" title="Pobierz Umowę PDF" @click="downloadPDF(item)">
                                    <i class="bi bi-file-earmark-pdf"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger" title="Usuń" @click="deleteContract(item.id || item.ID)">
                                    <i class="bi bi-trash"></i>
                                </button>
                            </div>
                        </template>
                    </Vue3EasyDataTable>
                </div>
            </div>
        </div>

        <!-- MODAL TWORZENIA / EDYCJI -->
        <div ref="contractModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">Nowa umowa sprzedaży</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="modal-body p-4">
                        <form @submit.prevent>
                            <DepositSection1 v-model="form" title="Dane dokumentu" :lastDocuments="lastContracts" />

                            <InvoiceSection2 v-model="form.vehicle" :vehicleAddons="[]" 
                                    @openVehicleSelector="openVehicleSelector" 
                                    title="Dane pojazdu"
                            />
                            <VehicleSelectorModal ref="vehicleSelectorModalRef" @select="onVehicleSelected" />

                            <SaleSectionVehicleState v-model="form" />

                            <InvoiceSection4 v-model="form.seller" :dicts="dicts" title="Sprzedawca" />
                            <InvoiceSection5 v-model="form.buyer" :dicts="dicts" @fetchGus="fetchGusData" title="Nabywca" />
                            <InvoiceSection6 v-model="form.coOwner" :dicts="dicts" title="Współwłaściciel" />

                            <SaleSection7 v-model="form" v-model:sellerModel="form.seller" :dicts="dicts" 
                                    title="Cena i płatność" />

                            <SaleSectionProvisions v-model="form" />
                            <DepositSectionNotes v-model="form.notes" />

                            <div v-if="missingWarnings.length > 0" class="alert alert-warning mb-3">
                                <div class="fw-bold mb-1">W dokumencie brakuje istotnych danych (nie są obowiązkowe) - zweryfikuj brakujące pola:</div>
                                <ul class="mb-0 ps-3">
                                    <li v-for="warn in missingWarnings" :key="warn" class="fst-italic">{{ warn }}</li>
                                </ul>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer bg-light d-flex justify-content-between flex-wrap gap-2">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Anuluj</button>
                        <div class="d-flex gap-2">
                            <button type="button" class="btn btn-success" :disabled="saving" @click="saveContract">
                                <i class="bi bi-save me-1"></i>Zapisz
                            </button>
                            
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <AppLoader :show="saving || loading" :text="loaderText" />

        <!-- Ukryty kontener do generowania PDF za pomocą html2pdf -->
        <div style="position: absolute; left: -9999px; top: -9999px;">
            <SaleContractTemplate ref="contractPdfRenderArea" v-if="pdfContractDetails" :contract="pdfContractDetails" />
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue'
import html2pdf from 'html2pdf.js'
import apiClient from '@/api'
import Modal from 'bootstrap/js/dist/modal'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import AppLoader from '@/components/AppLoader.vue'
import { webAlert, showConfirm } from '@/utils/dialog'

import InvoiceSection2 from '@/components/invoices/InvoiceSection2.vue'
import InvoiceSection5 from '@/components/invoices/InvoiceSection5.vue'
import InvoiceSection4 from '@/components/invoices/InvoiceSection4.vue'
import InvoiceSection6 from '@/components/invoices/InvoiceSection6.vue'
import VehicleSelectorModal from '@/components/invoices/VehicleSelectorModal.vue'
import SaleContractTemplate from '@/components/templates/SaleContractTemplate.vue'
import SaleSection7 from '../components/sale/SaleSection7.vue'

import DepositSection1 from '@/components/deposit/DepositSection1.vue'
import DepositSectionNotes from '@/components/deposit/DepositSectionNotes.vue'

import SaleSectionVehicleState from '@/components/sale/SaleSectionVehicleState.vue'
import SaleSectionProvisions from '@/components/sale/SaleSectionProvisions.vue'

const pdfContractDetails = ref(null)
const contractPdfRenderArea = ref(null)
const lastContracts = ref([])

const loading = ref(false)
const saving = ref(false)
const loaderText = ref('')
const contracts = ref([])
const contractModalRef = ref(null)
let contractModalInstance = null
const vehicleSelectorModalRef = ref(null)

const headers = [
    { text: 'Numer umowy', value: 'docNumber', sortable: true },
    { text: 'Nabywca', value: 'companyName', sortable: true }, // lub dowolny unikalny klucz
    { text: 'Kwota pojazdu', value: 'carPrice', sortable: true },
    { text: 'Data wystawienia', value: 'issueDate', sortable: true },
    { text: 'Akcje', value: 'actions', width: 180 }
]

const dicts = reactive({
    currencies: [],
    countries: [],
    paymentTypes: []
})

const fetchDict = async (action, extraData = {}) => {
    try {
        const res = await apiClient.post({ action, ...extraData })
        return Array.isArray(res.data?.data) ? res.data.data : []
    } catch { return [] }
}

const fetchLastContracts = async () => {
    try {
        const response = await apiClient.post({ action: 'get-last-sale-contracts' }) // zmień akcję na odpowiednią w swoim API, jeśli nazwa jest inna
        const rawData = response.data?.data || response.data || []
        
        lastContracts.value = rawData.map(item => {
            if (typeof item === 'object' && item !== null) {
                return {
                    docNumber: item.docNumber,
                    createDate: item.issueDate || item.createDate
                }
            }
            return { docNumber: item, createDate: '' }
        })
    } catch (error) {
        console.error('Błąd pobierania ostatnich umów:', error)
        lastContracts.value = []
    }
}

const loadDictionaries = async () => {
    dicts.currencies = await fetchDict('get-currencies')
    dicts.countries = await fetchDict('get-countries')
    dicts.paymentTypes = await fetchDict('get-payment-types')
}

const createEmptyParty = () => ({
    nip: '', companyName: '', firstName: '', lastName: '', country: 'Polska',
    postalCode: '', city: '', street: '', email: '', phone: '', pesel: '',
    idCardSeriesNumber: '', notes: '', bankName: '', bankAccount: ''
})

const defaultSaleProvisions = `1. W niniejszej Umowie: (1) wyraz lub wyrazy użyte w liczbie pojedynczej obejmują liczbę mnogą i odwrotnie; (2) nagłówki użyte w niniejszej Umowie służą wyłącznie przejrzystości tekstu i nie będą stanowić podstawy wykładni postanowień niniejszej Umowie; (3) wskazanie dni, tygodni, miesięcy lub lat do określenia, oznacza dni, tygodnie, miesiące lub lata kalendarzowe; (4) odniesienie do opóźnienia lub zwłoki w wykonaniu Umowy; a także do niewykonania lub nienależytego wykonania Umowy, dotyczy zarówno części, jak i całości przedmiotu Umowy, chyba że zastrzeżono inaczej; (5) terminy pisane wielką literą, mają takie znaczenia, jakie nadano im w niniejszej Umowie.
2. Umowę sporządzono w dwóch jednobrzmiących egzemplarzach, po jednym dla każdej ze Stron.
3. Komis będący Pośrednikiem nie jest stroną niniejszej Umowy Sprzedaży. Wszelkie podpisy lub pieczęcie Pośrednika (Komisu) lub osoby w niej pracującej złożone na niniejszej Umowie należy uważać za złożone w związku z czynnościami pośrednictwa. Jeżeli w niniejszej Umowie składane są oświadczenia woli podpisane i opieczętowane przez Pośrednika (Komis)- są dokonywane one wyłącznie w imieniu i na rzecz Sprzedawcy na podstawie pełnomocnictwa udzielonego w odrębnym dokumencie.`

const createEmptyForm = () => ({
    ID: null,
    docNumber: '',
    correctedInvoiceNumber: '',
    issueDate: new Date().toISOString().substring(0, 10),
    issuePlace: '',
    vehicle: {
        id: null, 
        make: '', 
        model: '', 
        engineCapacity: '', 
        mileage: '', 
        gearbox: '',
        fuelType: '', 
        powerHp: '', 
        color: '', 
        vin: '', 
        type: '',
        registrationPlate: '',
        productionYear: '', 
        firstRegistrationDate: '', 
        additionalInfo: ''
    },
    ocState: '',
    keysState: '',
    regBookState: '',
    regStickerState: '',
    platesState: '',
    carCardState: '',
    exciseTaxState: '',
    serviceBookState: '',
    additionalEquipment: '',
    vehicleStateNotes: '',
    seller: createEmptyParty(),
    coOwner: createEmptyParty(),
    buyer: createEmptyParty(),
    carPrice: 0,
    carCurrency: 'PLN',
    depositAmount: 0,
    depositCurrency: 'PLN',
    agreementEndDate: '',
    
    // Lista płatności (wzorowana na fakturach - pozwala podzielić kwotę na kilka sposobów)
    payments: [
        {
            amount: 0,
            paymentType: 'Gotówka',
            dueDate: '',
            currency: 'PLN',
            bankName: '',
            bankAccount: ''
        }
    ],
    privatePurpose: false,
    businessPurpose: false,
    pccTax: false,
    vatTax: false,
    pricePaidOnSigning: false,
    paymentTermDays: 0,
    provisions: defaultSaleProvisions,
    notes: ''
})

const form = reactive(createEmptyForm())

const missingWarnings = computed(() => {
    const warnings = []
    if (!form.vehicle.make) warnings.push('brak podanej marki pojazdu')
    if (!form.carPrice || Number(form.carPrice) <= 0) warnings.push('brak ceny sprzedaży')
    //if (!form.paymentTermDays && !form.pricePaidOnSigning) warnings.push('brak typu płatności')
    if (!form.issueDate) warnings.push('brak daty sprzedaży')
    return warnings
})

const loadContracts = async () => {
    loading.value = true
    loaderText.value = 'Pobieranie danych z serera ...'

    try {
        const response = await apiClient.post({ action: 'get-sale-contracts' })
        contracts.value = response.data?.data || []
    } catch (e) {
        webAlert('Błąd podczas pobierania umów sprzedaży.')
    } finally {
        loading.value = false
    }
}

const openVehicleSelector = () => vehicleSelectorModalRef.value?.show()

const onVehicleSelected = (vehicleData) => {
    Object.assign(form.vehicle, {
        id: vehicleData.ID || vehicleData.id,
        make: vehicleData.make || '',
        model: vehicleData.model || '',
        engineCapacity: vehicleData.engineCCM || vehicleData.engineCapacity || '',
        mileage: vehicleData.mileage || '',
        type: vehicleData.type || '',
        gearbox: vehicleData.gearboxTXT || vehicleData.gearbox || '',
        fuelType: vehicleData.fuelTXT || vehicleData.fuelType || '',
        powerHp: vehicleData.powerKM || vehicleData.powerHp || '',
        color: vehicleData.colourTXT || vehicleData.color || '',
        vin: vehicleData.VIN || vehicleData.vin || '',
        registrationPlate: vehicleData.registerNumber || vehicleData.registrationPlate || '',
        productionYear: vehicleData.productionYear || '',
        firstRegistrationDate: vehicleData.firstRegistrationDate || '',
        additionalInfo: vehicleData.additionalInfo || vehicleData.offerDesc || ''
    })
    if (vehicleData.price) form.carPrice = Number(vehicleData.price)
}

const fetchGusData = async (type) => {
    const target = form[type]
    if (!target.nip) return
    saving.value = true
    loaderText.value = 'Pobieranie z GUS...'
    try {
        const res = await apiClient.post({ action: 'get_data_from_gus', nip: target.nip })
        if (res.data?.success) {
            Object.assign(target, res.data.company)
            if (type === 'seller') form.issuePlace = target.city
        }
    } finally {
        saving.value = false
    }
}

const openModalForCreate = () => {
    Object.assign(form, createEmptyForm())
    fetchLastContracts() // Pobierz 3 ostatnie umowy
    contractModalInstance.show()
}

const editContract = async (item) => {
    saving.value = true
    loaderText.value = 'Pobieranie danych umowy...'
    try {
        const response = await apiClient.post({ action: 'get-sale-contract', ID: item.id || item.ID })
        const data = response.data?.data?.[0] || response.data?.data || response.data
        if (data) {
            data.pricePaidOnSigning = data.pricePaidOnSigning === '1' || data.pricePaidOnSigning === 1 || data.pricePaidOnSigning === true
            data.privatePurpose = data.privatePurpose === '1' || data.privatePurpose === 1 || data.privatePurpose === true
            data.businessPurpose = data.businessPurpose === '1' || data.businessPurpose === 1 || data.businessPurpose === true
            data.pccTax = data.pccTax === '1' || data.pccTax === 1 || data.pccTax === true
            data.vatTax = data.vatTax === '1' || data.vatTax === 1 || data.vatTax === true
            
            Object.assign(form, createEmptyForm(), data)
            // Jeśli backend zwraca tablicę płatności, mapujemy ją
            if (Array.isArray(data.payment) && data.payment.length > 0) {
                form.payments = data.payment.map(pay => ({
                    amount: pay.amount || data.carPrice || 0,
                    paymentType: pay.paymentType || 'Gotówka',
                    dueDate: pay.dueDate && pay.dueDate !== '0000-00-00' ? pay.dueDate : '',
                    currency: pay.currency || data.carCurrency || 'PLN',
                    bankName: pay.bankName || '',
                    bankAccount: pay.bankAccount || ''
                }))
            }
            onVehicleSelected(data?.car);
        }
        contractModalInstance.show()
    } catch (error) {
        webAlert('Nie udało się pobrać danych umowy.')
    } finally {
        saving.value = false
    }
}

const saveContract = async () => {
    saving.value = true
    loaderText.value = 'Zapisywanie umowy...'
    try {
        console.log(form);
        const response = await apiClient.post({ action: 'save-sale-contract', contract: form })
        
        // Czasami axios wrzuca odpowiedź do response.data, a czasami backend zwraca obiekt bezpośrednio w response
        const resData = response.data || response

        // Sprawdzamy status lub obecność contractID / success
        if (resData?.status === 'success' || resData?.success || resData?.contractID) {
            contractModalInstance.hide()
            await loadContracts()
            webAlert({ message: resData?.message || 'Umowa sprzedaży została zapisana.', type: 'success' })
        } else {
            console.warn('Serwer zwrócił nietypową odpowiedź:', resData)
            webAlert(typeof resData === 'string' ? resData : 'Błąd podczas zapisywania umowy.')
        }
    } catch (e) {
        console.error('Szczegół błędu zapisu:', e)
        webAlert('Wystąpił błąd podczas połączenia z serwerem.')
    } finally {
        saving.value = false
    }
}


// Główna funkcja pobierająca dane umowy i otwierająca podgląd PDF
const downloadPDF = async (item) => {
    saving.value = true
    loaderText.value = 'Pobieranie danych umowy i generowanie PDF...'
    try {
        const response = await apiClient.post({ action: 'get-sale-contract', ID: item.id || item.ID })
        // Bezpieczne wyłuskanie obiektu umowy z odpowiedzi API
        const data = response.data?.data?.[0] || response.data?.data || response.data

        if (!data) {
            webAlert('Nie udało się odnaleźć danych wybranej umowy.')
            return
        }

        pdfContractDetails.value = data
        await nextTick()

        const opt = {
            margin: 5,
            filename: `Umowa_Sprzedazy_${data.docNumber || item.ID}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        }

        const element = contractPdfRenderArea.value?.$el || contractPdfRenderArea.value?.element
        
        if (!element) {
            webAlert('Nie odnaleziono elementu DOM do wygenerowania PDF.')
            return
        }

        const pdfWorker = html2pdf().set(opt).from(element)
        const pdfBlob = await pdfWorker.output('blob')

        const blobUrl = URL.createObjectURL(pdfBlob)
        window.open(blobUrl, '_blank')

    } catch (error) {
        console.error('Błąd generowania PDF:', error)
        webAlert('Błąd podczas generowania pliku PDF umowy.')
    } finally {
        saving.value = false
    }
}
 

const deleteContract = async (id) => {
    const confirmed = await showConfirm('Czy na pewno chcesz usunąć tę umowę?', {
        title: 'Usuwanie pozycji',
        confirmText: 'Usuń',
        cancelText: 'Anuluj',
        confirmClass: 'btn-danger',
        icon: 'bi-trash text-danger'
    })

    if (!confirmed) return

    saving.value = true
    loaderText.value = "Kasowanie ..."
    try {
        const response = await apiClient.post(
            {
                action: 'delete-sale-contract',
                id: id
            }
        )

        saving.value = false; 
        await loadContracts() // Odświeżenie tabeli po usunięciu
        webAlert(response.data)
    } catch (error) {
        saving.value = false; 
        webAlert('Błąd podczas usuwania pozycji.')
    } finally {
        saving.value = false;
    }
}

onMounted(async () => {
    await loadDictionaries()
    contractModalInstance = new Modal(contractModalRef.value)
    await loadContracts()
})
</script>

<style scoped>
.cursor-pointer { cursor: pointer; user-select: none; }
</style>